// RegistrationPage.jsx — Öffentliche Registrierung (Einzelsportler oder Verein)
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import './RegistrationPage.css';
import PasswortRegeln from '../components/passwort/PasswortRegeln';
import { passwortFehler } from '../lib/passwortRegel';

const GURTFARBEN = [
  'Weiß', 'Gelb', 'Orange', 'Grün', 'Blau', 'Rot', 'Braun', 'Schwarz', 'Dan 1', 'Dan 2', 'Dan 3',
];

// ── Vereinssuche mit Debounce ────────────────────────────────────────────────
function VereinSearch({ onSelect, selected }) {
  const [query, setQuery]     = useState(selected?.name ?? '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const [notFound, setNotFound] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); setOpen(false); setNotFound(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`/api/athlete/vereine/search?q=${encodeURIComponent(q)}`);
        const d = await r.json();
        if (d.success) {
          setResults(d.data);
          setNotFound(d.data.length === 0);
          setOpen(true);
        }
      } finally { setLoading(false); }
    }, 300);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (selected) onSelect(null);
    search(val);
  };

  const handleSelect = (v) => {
    setQuery(v.name);
    setResults([]);
    setOpen(false);
    setNotFound(false);
    onSelect(v);
  };

  return (
    <div className="rp-verein-search" ref={wrapRef}>
      <div className="rp-input-wrap">
        <input
          className={`rp-input${selected ? ' rp-input--verified' : ''}`}
          type="text"
          placeholder="Vereinsname eingeben…"
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          autoComplete="off"
        />
        {loading && <span className="rp-input-spinner" />}
        {selected && <span className="rp-input-check">✓</span>}
      </div>

      {open && results.length > 0 && (
        <div className="rp-dropdown">
          {results.map(v => (
            <div key={v.vereins_id} className="rp-dropdown-item" onClick={() => handleSelect(v)}>
              <span className="rp-dropdown-name">{v.name}</span>
              {v.ort && <span className="rp-dropdown-sub">{v.plz} {v.ort}</span>}
            </div>
          ))}
        </div>
      )}

      {notFound && query.length >= 2 && !selected && (
        <div className="rp-verein-notfound">
          <span className="rp-notfound-icon">⚠</span>
          <span>
            <strong>Verein nicht gefunden.</strong> Euer Verein ist noch nicht bei TDA registriert.{' '}
            Bitte informiert euren Vereinsverantwortlichen oder{' '}
            <a href="mailto:info@tda-intl.com" className="rp-link">kontaktiert TDA</a>.
          </span>
        </div>
      )}

      {selected && (
        <div className="rp-verein-confirmed">
          <span className="rp-confirmed-icon">✅</span>
          <div>
            <strong>{selected.name}</strong>
            {selected.ort && <span className="rp-confirmed-sub"> · {selected.plz} {selected.ort}</span>}
          </div>
          <button className="rp-btn-text" onClick={() => { setQuery(''); onSelect(null); setNotFound(false); }}>
            Ändern
          </button>
        </div>
      )}
    </div>
  );
}

// ── Schritt-Indikator ────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  const steps = ['Art wählen', 'Persönliche Daten', 'Verein', 'Fertig'];
  return (
    <div className="rp-steps">
      {steps.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`rp-step${step === i ? ' rp-step--active' : ''}${step > i ? ' rp-step--done' : ''}`}>
            <div className="rp-step-dot">{step > i ? '✓' : i + 1}</div>
            <span className="rp-step-label">{label}</span>
          </div>
          {i < steps.length - 1 && <div className={`rp-step-line${step > i ? ' rp-step-line--done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Formular-Validierung ─────────────────────────────────────────────────────
function validate(form) {
  const errs = {};
  if (!form.vorname?.trim())    errs.vorname    = 'Vorname erforderlich';
  if (!form.nachname?.trim())   errs.nachname   = 'Nachname erforderlich';
  if (!form.email?.trim())      errs.email      = 'E-Mail erforderlich';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Ungültige E-Mail';
  if (!form.passwort)           errs.passwort   = 'Passwort erforderlich';
  else if (passwortFehler(form.passwort)) errs.passwort = passwortFehler(form.passwort); // eine Regel für alle Konten (25.10.2026)
  if (form.passwort !== form.passwort2) errs.passwort2 = 'Passwörter stimmen nicht überein';
  if (!form.geburtsdatum)       errs.geburtsdatum = 'Geburtsdatum erforderlich';
  return errs;
}

// ── Hauptkomponente ──────────────────────────────────────────────────────────
export default function RegistrationPage() {
  const navigate = useNavigate();
  const [step, setStep]       = useState(0);   // 0=Wahl, 1=Persönlich, 2=Verein, 3=Fertig
  const [, setType]       = useState(null); // 'einzelsportler' | 'verein'
  const [form, setForm]       = useState({
    vorname: '', nachname: '', email: '', passwort: '', passwort2: '',
    geburtsdatum: '', geschlecht: '', gurtfarbe: ''
  });
  const [errors, setErrors]   = useState({});
  const [verein, setVerein]   = useState(null);
  const [submitting, setSub]  = useState(false);
  const [serverErr, setServerErr] = useState(null);

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setErrors(er => ({ ...er, [field]: undefined }));
  };

  // Schritt 0 → Wahl
  const chooseType = (t) => {
    if (t === 'verein') { navigate('/register'); return; }
    setType(t);
    setStep(1);
  };

  // Schritt 1 → Schritt 2
  const toStep2 = () => {
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setStep(2);
  };

  // Schritt 2 → Absenden
  const submit = async () => {
    if (!verein) { setServerErr('Bitte wähle deinen Verein aus.'); return; }
    setSub(true); setServerErr(null);
    try {
      const r = await fetch('/api/athlete/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vorname: form.vorname.trim(),
          nachname: form.nachname.trim(),
          email: form.email.trim(),
          passwort: form.passwort,
          geburtsdatum: form.geburtsdatum || null,
          geschlecht: form.geschlecht || null,
          gurtfarbe: form.gurtfarbe || null,
          vereins_id: verein.vereins_id,
        }),
      });
      const d = await r.json();
      if (d.success) { setStep(3); }
      else setServerErr(d.error || 'Registrierung fehlgeschlagen');
    } catch {
      setServerErr('Verbindungsfehler — bitte versuche es erneut');
    } finally { setSub(false); }
  };

  return (
    <div className="rp-page">
      <PublicNav />
      <div className="rp-container">

        {step < 3 && (
          <div className="rp-header">
            <h1 className="rp-title">Konto erstellen</h1>
            <p className="rp-sub">Bereits registriert? <button className="rp-btn-text" onClick={() => navigate('/anmelden')}>Jetzt einloggen</button></p>
          </div>
        )}

        {step > 0 && step < 3 && <StepIndicator step={step} />}

        {/* ━━ SCHRITT 0: ART WÄHLEN ━━ */}
        {step === 0 && (
          <div className="rp-choice">
            <p className="rp-choice-intro">Für wen möchtest du ein Konto anlegen?</p>
            <div className="rp-choice-grid">
              <button className="rp-choice-card" onClick={() => chooseType('einzelsportler')}>
                <div className="rp-choice-icon">🥋</div>
                <div className="rp-choice-name">Ich bin Einzelsportler</div>
                <div className="rp-choice-desc">
                  Du möchtest dich persönlich für Turniere anmelden, deine Ergebnisse verfolgen und dein Profil verwalten.
                </div>
                <div className="rp-choice-arrow">Konto erstellen →</div>
              </button>
              <button className="rp-choice-card" onClick={() => chooseType('verein')}>
                <div className="rp-choice-icon">🏟️</div>
                <div className="rp-choice-name">Ich vertrete einen Verein</div>
                <div className="rp-choice-desc">
                  Du möchtest deinen Verein anmelden und mehrere Athleten für Turniere registrieren.
                </div>
                <div className="rp-choice-arrow">Vereins-Konto erstellen →</div>
              </button>
            </div>
            <p className="rp-choice-note">
              Du hast bereits ein Konto?{' '}
              <button className="rp-btn-text" onClick={() => navigate('/anmelden')}>Einloggen</button>
            </p>
          </div>
        )}

        {/* ━━ SCHRITT 1: PERSÖNLICHE DATEN ━━ */}
        {step === 1 && (
          <div className="rp-form-card">
            <h2 className="rp-form-title">Persönliche Daten</h2>

            <div className="rp-row">
              <div className="rp-field">
                <label className="rp-label">Vorname *</label>
                <input className={`rp-input${errors.vorname ? ' rp-input--error' : ''}`}
                  type="text" value={form.vorname} onChange={set('vorname')}
                  placeholder="Max" autoComplete="given-name" />
                {errors.vorname && <span className="rp-error">{errors.vorname}</span>}
              </div>
              <div className="rp-field">
                <label className="rp-label">Nachname *</label>
                <input className={`rp-input${errors.nachname ? ' rp-input--error' : ''}`}
                  type="text" value={form.nachname} onChange={set('nachname')}
                  placeholder="Mustermann" autoComplete="family-name" />
                {errors.nachname && <span className="rp-error">{errors.nachname}</span>}
              </div>
            </div>

            <div className="rp-field">
              <label className="rp-label">E-Mail-Adresse * <span className="rp-label-note">(wird als Login verwendet)</span></label>
              <input className={`rp-input${errors.email ? ' rp-input--error' : ''}`}
                type="email" value={form.email} onChange={set('email')}
                placeholder="max@beispiel.de" autoComplete="email" />
              {errors.email && <span className="rp-error">{errors.email}</span>}
            </div>

            <div className="rp-row">
              <div className="rp-field">
                <label className="rp-label">Passwort *</label>
                <input className={`rp-input${errors.passwort ? ' rp-input--error' : ''}`}
                  type="password" value={form.passwort} onChange={set('passwort')}
                  placeholder="••••••••" autoComplete="new-password" />
                {errors.passwort && <span className="rp-error">{errors.passwort}</span>}
                <PasswortRegeln passwort={form.passwort} />
              </div>
              <div className="rp-field">
                <label className="rp-label">Passwort wiederholen *</label>
                <input className={`rp-input${errors.passwort2 ? ' rp-input--error' : ''}`}
                  type="password" value={form.passwort2} onChange={set('passwort2')}
                  placeholder="••••••••" autoComplete="new-password" />
                {errors.passwort2 && <span className="rp-error">{errors.passwort2}</span>}
              </div>
            </div>

            <div className="rp-row">
              <div className="rp-field">
                <label className="rp-label">Geburtsdatum *</label>
                <input className={`rp-input${errors.geburtsdatum ? ' rp-input--error' : ''}`}
                  type="date" value={form.geburtsdatum} onChange={set('geburtsdatum')}
                  max={new Date().toISOString().split('T')[0]} />
                {errors.geburtsdatum && <span className="rp-error">{errors.geburtsdatum}</span>}
              </div>
              <div className="rp-field">
                <label className="rp-label">Geschlecht</label>
                <select className="rp-input" value={form.geschlecht} onChange={set('geschlecht')}>
                  <option value="">Bitte wählen…</option>
                  <option value="männlich">Männlich</option>
                  <option value="weiblich">Weiblich</option>
                </select>
              </div>
            </div>

            <div className="rp-field">
              <label className="rp-label">Gürtelgrad / Graduierung</label>
              <select className="rp-input" value={form.gurtfarbe} onChange={set('gurtfarbe')}>
                <option value="">Bitte wählen…</option>
                {GURTFARBEN.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div className="rp-actions">
              <button className="rp-btn-ghost" onClick={() => setStep(0)}>← Zurück</button>
              <button className="rp-btn-primary" onClick={toStep2}>Weiter →</button>
            </div>
          </div>
        )}

        {/* ━━ SCHRITT 2: VEREIN ━━ */}
        {step === 2 && (
          <div className="rp-form-card">
            <h2 className="rp-form-title">Dein Verein</h2>
            <p className="rp-form-intro">
              Der Verein ist Pflichtfeld. Gib den Namen deines Vereins ein — wenn er im TDA-System gefunden wird,
              werden die Daten automatisch übernommen.
            </p>

            <div className="rp-field">
              <label className="rp-label">Verein suchen *</label>
              <VereinSearch onSelect={setVerein} selected={verein} />
            </div>

            {serverErr && <div className="rp-server-error">⚠ {serverErr}</div>}

            <div className="rp-actions">
              <button className="rp-btn-ghost" onClick={() => { setStep(1); setServerErr(null); }}>← Zurück</button>
              <button
                className="rp-btn-primary"
                onClick={submit}
                disabled={!verein || submitting}
              >
                {submitting ? 'Wird registriert…' : 'Konto erstellen ✓'}
              </button>
            </div>
          </div>
        )}

        {/* ━━ SCHRITT 3: ERFOLG ━━ */}
        {step === 3 && (
          <div className="rp-success">
            <div className="rp-success-icon">🎉</div>
            <h2 className="rp-success-title">Willkommen, {form.vorname}!</h2>
            <p className="rp-success-text">
              Dein Athleten-Konto wurde erfolgreich erstellt. Du kannst dich jetzt einloggen,
              dein Profil vervollständigen und dich für Turniere anmelden.
            </p>
            <div className="rp-success-actions">
              <button className="rp-btn-primary rp-btn-large" onClick={() => navigate('/anmelden')}>
                Jetzt einloggen →
              </button>
              <button className="rp-btn-ghost" onClick={() => navigate('/')}>
                Zur Startseite
              </button>
            </div>
          </div>
        )}

      </div>

      <footer className="rp-footer">
        <div className="rp-footer-inner">
          <a href="/">← Zurück</a>
          <span>© {new Date().getFullYear()} TDA International</span>
        </div>
      </footer>
    </div>
  );
}
